import React, { useState } from 'react';
import useEsgScore from '../../hooks/useEsgScore';
import useHasMounted from '../../hooks/useHasMounted';
import InfoTooltip from '../InfoTooltip/InfoTooltip';
import style from './About.module.css';

const About = ({ about, stock }) => {
  const [showMore, setShowMore] = useState(false);
  const hasMounted = useHasMounted();
  const { esg_score } = useEsgScore(stock?.id);

  const name = about?.name;
  const employees = about?.employees;
  const description = about?.description;
  const ceo = about?.ceo;

  const hasEsgData =
    esg_score &&
    Object.keys(esg_score).length > 0 &&
    esg_score.esg_score != null;
  const hasEsg = hasMounted && hasEsgData;

  const esgTooltip = hasEsg ? (
    <div className={style.esgTooltip}>
      <p className={style.esgTooltipIntro}>
        Environmental, social, and governance risks. Lower score means less risk.
      </p>
      <ul className={style.esgTooltipList}>
        <li>
          <span>Environment</span>
          <span>{formatScore(esg_score.environmental_score)} / 100</span>
        </li>
        <li>
          <span>Social</span>
          <span>{formatScore(esg_score.social_score)} / 100</span>
        </li>
        <li>
          <span>Governance</span>
          <span>{formatScore(esg_score.governance_score)} / 100</span>
        </li>
        <li className={style.esgTooltipTotal}>
          <span>Total</span>
          <span>{formatScore(esg_score.esg_score)} / 100</span>
        </li>
      </ul>
    </div>
  ) : null;

  if (about === null) {
    return (
      <div data-testid="stock-about">
        <h3 className={style.headline}>About</h3>
        <p className={style.noData}>No data</p>
      </div>
    );
  }

  return (
    <div data-testid="stock-about">
      {name && <h3 className={style.headline}>About {name}</h3>}
      {hasEsg && (
        <div className={style.iconDiv}>
          <InfoTooltip content={esgTooltip} label="ESG score breakdown">
            <span className={style.esgTrigger}>
              <img
                src="/react/phosphor/factory-duotone.svg"
                className={style.iconPicture}
                alt=""
                aria-hidden="true"
              />
              <span className={style.esgTriggerText}>
                ESG score {formatScore(esg_score.esg_score)} / 100
              </span>
            </span>
          </InfoTooltip>
        </div>
      )}
      {employees && (
        <div className={style.iconDiv}>
          <img src="/react/phosphor/users-three-duotone.svg" className={style.iconPicture} alt="" aria-hidden="true" />
          <span className={style.iconText}>{numberWithCommas(employees)} employees</span>
        </div>
      )}
      {ceo && (
        <div className={style.iconDiv}>
          <img src="/react/phosphor/user-circle-duotone.svg" className={style.iconPicture} alt="" aria-hidden="true" />
          <span className={style.iconText}>CEO: {ceo}</span>
        </div>
      )}
      {description && (
        <p className={style.text}>
          {showMore ? description : shorten(description, 240)}
          {description.length > 300 && (
            <button className={style.more} type="button" onClick={() => setShowMore(!showMore)}>
              {showMore ? 'Show less' : 'Show more'}
            </button>
          )}
        </p>
      )}
    </div>
  );
};

function formatScore(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return '-';
  }
  return Number(value).toFixed();
}

function shorten(text, maxLength) {
  if (text.length > maxLength + 60) {
    return `${text.substr(0, maxLength - 3)}...`;
  }
  return text;
}

function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export default About;
